import { useEffect, useState } from "react"
import type { AvatarModel, AvatarPolarity } from "@domo/shared"
import { SOCKET_EVENTS } from "@domo/shared"
import { getSocket } from "../../lib/socket"
import { useIsMobile } from "../../hooks/useDevice"
import { usePlayerStore } from "../../stores/usePlayerStore"

// Sprint 5 — Selector de encarnación con doble eje:
//
//   · Eje de DENSIDAD (Material): Cristal · Plasma · Biopolímero
//   · Eje de POLARIDAD (Estructura): Alfa (eléctrico) · Omega (magnético)
//
// Aparece como overlay glassmorphism cuando el Tripulante está en void
// (recién conectado y sin firma persistida en su perfil). Al confirmar
// emite SPAWN_REQUEST con material + polarity. El server la guarda en
// public.profiles para que próximos accesos vayan directo a auto-
// encarnación sin pasar por aquí.
//
// Si el server detecta firma previa al conectar, dispara SPAWN_EVENT
// auto-encarnado mientras este selector aún está en pantalla — el
// listener de useSocket cambia phase a "descending" y este componente
// se desmonta solo (return null cuando phase !== "void").

interface MaterialDef {
    id: AvatarModel
    title: string
    subtitle: string
    palette: { primary: string; halo: string }
    description: string
}

interface PolarityDef {
    id: AvatarPolarity
    title: string
    subtitle: string
    aura: string // color del aura del orbe en la card
    accent: string
    description: string
}

// 2026-05-05 — Descripciones reducidas a la última frase "Resuena con
// X" (la línea esencial de la firma). Las dos frases anteriores
// describían textura visual y sumaban scroll en mobile cuando las 3
// cards apilan verticalmente. Esta línea sola comunica el rasgo
// vibracional del material y libera espacio.
const MATERIALS: MaterialDef[] = [
    {
        id: "cristal",
        title: "Cristal",
        subtitle: "Geometría pura",
        palette: { primary: "#9CF6FF", halo: "#00E5FF" },
        description: "Resuena con claridad mental.",
    },
    {
        id: "plasma",
        title: "Plasma",
        subtitle: "Energía viva",
        palette: { primary: "#FFD18C", halo: "#FFB347" },
        description: "Resuena con calor emocional.",
    },
    {
        id: "biopolimero",
        title: "Biopolímero",
        subtitle: "Tejido orgánico",
        palette: { primary: "#E2C8FF", halo: "#B57BFF" },
        description: "Resuena con presencia corporal.",
    },
]

const POLARITIES: PolarityDef[] = [
    {
        id: "alfa",
        title: "Alfa",
        subtitle: "Eléctrico · Masculino",
        aura: "#7BD7FF",
        accent: "#00C2FF",
        description:
            "Pulso ascendente. Geometría angular, rotación horaria. La firma del rayo que penetra y define.",
    },
    {
        id: "omega",
        title: "Omega",
        subtitle: "Magnético · Femenino",
        aura: "#FFB0E5",
        accent: "#FF6BAA",
        description:
            "Onda descendente. Geometría curva, rotación antihoraria. La firma del campo que envuelve y atrae.",
    },
]

export function IncarnationSelector() {
    const phase = usePlayerStore((s) => s.phase)
    const voidStage = usePlayerStore((s) => s.voidStage)
    const [pickMaterial, setPickMaterial] = useState<AvatarModel | null>(null)
    const [pickPolarity, setPickPolarity] = useState<AvatarPolarity | null>(
        null
    )
    const [confirming, setConfirming] = useState(false)

    // 2026-05-05 — Mobile: flujo en DOS fases secuenciales sin scroll.
    // La pantalla del [LENTE] no alcanza para mostrar las 5 cards (3
    // densidad + 2 polaridad) más header + botón sin scroll. Dividimos
    // el rito en dos pasos:
    //   · phase "density"  — solo se ven las 3 cards de densidad.
    //   · phase "polarity" — se ven las 2 cards de polaridad + un
    //                        botón ← para volver a cambiar la densidad.
    // Al picar densidad se transiciona automáticamente a "polarity"
    // con un fade. Desktop renderea las dos secciones a la vez como
    // antes (la grid responsive cabe sin scroll en md+).
    const isMobile = useIsMobile()
    const [mobilePhase, setMobilePhase] = useState<"density" | "polarity">(
        "density"
    )

    // Sprint 5 fix — Cuando el Tripulante vuelve al void desde el menú
    // de reconfiguración, el componente sigue montado (solo retornaba
    // null cuando phase != "void") y el state interno se quedaba con
    // `confirming = true` del descenso anterior. Resultado: el botón
    // mostraba "DESCENDIENDO…" eterno aunque ya estuviera de vuelta
    // en void. Reseteamos también las picks para que el rito de
    // reconfiguración arranque limpio.
    useEffect(() => {
        if (phase === "void") {
            setConfirming(false)
            setPickMaterial(null)
            setPickPolarity(null)
            setMobilePhase("density")
        }
    }, [phase])

    // Mobile: cuando el Tripulante pica una densidad, transicionamos
    // automáticamente al eje de polaridad. Sin esto el Tripulante
    // se queda viendo la card seleccionada sin saber que sigue.
    useEffect(() => {
        if (isMobile && pickMaterial) {
            setMobilePhase("polarity")
        }
    }, [isMobile, pickMaterial])

    // Sprint 6 — Solo aparece cuando ya pasó la intro narrativa del
    // Estado Cero (voidStage === "selector"). Durante "intro" el
    // VoidIntro overlay tiene la pantalla.
    if (phase !== "void" || voidStage !== "selector") return null

    const ready = pickMaterial && pickPolarity

    const onConfirm = () => {
        if (!ready || confirming) return
        const socket = getSocket()
        if (!socket?.connected) return
        setConfirming(true)
        socket.emit(SOCKET_EVENTS.SPAWN_REQUEST, {
            model: pickMaterial,
            polarity: pickPolarity,
        })
    }

    return (
        <div
            className="fixed inset-0 z-30 overflow-y-auto rsv-no-scrollbar"
            style={{
                background:
                    "radial-gradient(circle at 50% 50%, rgba(8,12,28,0.55) 0%, rgba(0,0,0,0.78) 100%)",
                backdropFilter: "blur(14px) saturate(140%)",
                WebkitBackdropFilter: "blur(14px) saturate(140%)",
                userSelect: "none",
                WebkitUserSelect: "none",
                // Sprint 5 — Inmersividad: nada de scrollbar visible.
                // Firefox: scrollbar-width none; Webkit: regla en
                // <style> embebido (los pseudo-selectores no caben
                // en `style` inline).
                scrollbarWidth: "none",
                msOverflowStyle: "none",
            }}
        >
            <style>{`
                .rsv-no-scrollbar::-webkit-scrollbar {
                    display: none;
                    width: 0;
                    height: 0;
                }
            `}</style>
            <style>{`
                @keyframes selectorFade {
                    0% { opacity: 0; transform: translateY(8px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                .selector-section-fade {
                    animation: selectorFade 360ms cubic-bezier(0.2, 0.7, 0.2, 1) both;
                }
            `}</style>
            <div className="min-h-full flex items-center justify-center px-4 md:px-6 py-5 md:py-10">
                <div className="w-full max-w-5xl flex flex-col items-center gap-4 md:gap-10">
                    {/* 2026-05-05 — Header en DOS líneas (FASE DE
                        ENCARNACIÓN / ELIGE TU FIRMA) para que en mobile
                        no se corte horizontalmente. El h1 "Densidad y
                        polaridad de tu descenso" se eliminó (ruido
                        adicional) y el copy del párrafo cambia
                        "el sistema recordará" → "el Domo recordará"
                        para alinear al naming del proyecto. */}
                    <header className="text-center">
                        <p
                            className="text-xs tracking-[0.32em] leading-relaxed"
                            style={{ color: "rgba(180,220,255,0.85)" }}
                        >
                            FASE DE ENCARNACIÓN
                            <br />
                            ELIGE TU FIRMA
                        </p>
                        <p
                            className="text-sm md:text-base mt-4 max-w-2xl mx-auto"
                            style={{ color: "rgba(200,220,240,0.65)" }}
                        >
                            Tu elección es un sello. Quedará grabada en tu
                            perfil; en próximos cruces del portal el Domo
                            recordará tu firma y te materializará al instante.
                        </p>
                    </header>

                    {/* ── Eje de DENSIDAD ──
                        Mobile: solo se renderiza durante mobilePhase
                        "density". Desktop: siempre. */}
                    {(!isMobile || mobilePhase === "density") && (
                        <section
                            key="density"
                            className="w-full flex flex-col gap-3 md:gap-4 selector-section-fade"
                        >
                            <SectionHeading
                                ordinal="I"
                                title="Eje de Densidad"
                                subtitle="Material que sostiene tu forma"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 md:gap-5">
                                {MATERIALS.map((m) => (
                                    <MaterialCard
                                        key={m.id}
                                        def={m}
                                        selected={pickMaterial === m.id}
                                        disabled={confirming}
                                        onPick={() => setPickMaterial(m.id)}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {/* ── Eje de POLARIDAD ──
                        Mobile: solo durante mobilePhase "polarity",
                        con un botón ← para volver al eje de densidad
                        si el Tripulante quiere cambiar de material.
                        Desktop: siempre visible bajo el de densidad. */}
                    {(!isMobile || mobilePhase === "polarity") && (
                        <section
                            key="polarity"
                            className="w-full flex flex-col gap-4 selector-section-fade"
                        >
                            {isMobile && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMobilePhase("density")
                                    }}
                                    disabled={confirming}
                                    className="self-start text-xs tracking-[0.24em] py-2 px-3 rounded-full"
                                    style={{
                                        color: "rgba(180,220,255,0.85)",
                                        background:
                                            "rgba(8,16,32,0.7)",
                                        border: "1px solid rgba(120,160,200,0.25)",
                                        cursor: confirming
                                            ? "not-allowed"
                                            : "pointer",
                                    }}
                                >
                                    ← Cambiar densidad
                                </button>
                            )}
                            <SectionHeading
                                ordinal="II"
                                title="Eje de Polaridad"
                                subtitle="Estructura vibracional de tu firma"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                                {POLARITIES.map((p) => (
                                    <PolarityCard
                                        key={p.id}
                                        def={p}
                                        selected={pickPolarity === p.id}
                                        disabled={confirming}
                                        onPick={() =>
                                            setPickPolarity(p.id)
                                        }
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Botón CONFIRMAR. En desktop siempre se ve (la
                        deshabilitación visual marca el progreso). En
                        mobile solo aparece en la fase de polaridad
                        para no contaminar la fase de densidad. */}
                    {(!isMobile || mobilePhase === "polarity") && (
                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={!ready || confirming}
                            style={{
                                background: ready
                                    ? "linear-gradient(90deg, rgba(0,194,255,0.85), rgba(0,229,255,0.6))"
                                    : "rgba(40,52,72,0.6)",
                                color: ready
                                    ? "#04101A"
                                    : "rgba(180,200,220,0.5)",
                                boxShadow: ready
                                    ? "0 0 28px rgba(0,194,255,0.55)"
                                    : "none",
                                cursor:
                                    ready && !confirming
                                        ? "pointer"
                                        : "not-allowed",
                                transition:
                                    "background 220ms ease, box-shadow 220ms ease, color 220ms ease",
                            }}
                            className="px-10 py-3 rounded-full text-sm md:text-base tracking-[0.2em] font-medium"
                        >
                            {confirming
                                ? "DESCENDIENDO…"
                                : ready
                                  ? "CONFIRMAR DESCENSO"
                                  : "ELIGE DENSIDAD Y POLARIDAD"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

// ── Encabezado ordinal de cada sección ────────────────────
function SectionHeading({
    ordinal,
    title,
    subtitle,
}: {
    ordinal: string
    title: string
    subtitle: string
}) {
    return (
        <div className="flex items-baseline gap-3">
            <span
                className="text-xs tracking-[0.32em]"
                style={{ color: "rgba(0,194,255,0.6)" }}
            >
                {ordinal}.
            </span>
            <h2
                className="text-base md:text-lg font-light"
                style={{ color: "#cfeeff" }}
            >
                {title}
            </h2>
            <span
                className="text-xs"
                style={{ color: "rgba(180,200,220,0.55)" }}
            >
                · {subtitle}
            </span>
        </div>
    )
}

// ── Card de Material ──────────────────────────────────────
// 2026-05-05 — Layout responsive: mobile = fila horizontal compacta
// (glyph izquierda + texto derecha) para que las 3 cards apiladas
// quepan en la pantalla del [LENTE] sin scroll. Desktop conserva la
// columna vertical con glyph arriba como antes. Padding y glyph
// reducidos en mobile.
function MaterialCard({
    def,
    selected,
    disabled,
    onPick,
}: {
    def: MaterialDef
    selected: boolean
    disabled: boolean
    onPick: () => void
}) {
    return (
        <button
            type="button"
            onClick={onPick}
            disabled={disabled}
            style={cardStyle(def.palette.halo, selected, disabled)}
            className="rounded-2xl p-3 md:p-6 text-left flex flex-row md:flex-col items-center md:items-start gap-3 md:gap-4"
        >
            <ModelGlyph
                color={def.palette.primary}
                halo={def.palette.halo}
                selected={selected}
                kind={def.id}
                compact
            />
            <div className="flex-1 md:w-full min-w-0">
                <div
                    className="text-[9px] md:text-[10px] tracking-[0.24em] md:tracking-[0.28em] mb-0.5 md:mb-1"
                    style={{ color: hexA(def.palette.halo, 0.85) }}
                >
                    {def.subtitle.toUpperCase()}
                </div>
                <div
                    className="text-lg md:text-2xl font-light leading-tight"
                    style={{ color: "#E8F4FF" }}
                >
                    {def.title}
                </div>
                <p
                    className="text-xs md:text-sm leading-snug md:leading-relaxed mt-1 md:mt-2"
                    style={{ color: "rgba(200,220,240,0.72)" }}
                >
                    {def.description}
                </p>
            </div>
        </button>
    )
}

// ── Card de Polaridad ─────────────────────────────────────
function PolarityCard({
    def,
    selected,
    disabled,
    onPick,
}: {
    def: PolarityDef
    selected: boolean
    disabled: boolean
    onPick: () => void
}) {
    return (
        <button
            type="button"
            onClick={onPick}
            disabled={disabled}
            style={cardStyle(def.accent, selected, disabled)}
            className="rounded-2xl p-5 md:p-6 text-left flex flex-row md:flex-col items-center md:items-start gap-4"
        >
            <PolarityGlyph
                aura={def.aura}
                accent={def.accent}
                selected={selected}
                kind={def.id}
            />
            <div className="flex-1">
                <div
                    className="text-[10px] tracking-[0.28em] mb-1"
                    style={{ color: hexA(def.accent, 0.85) }}
                >
                    {def.subtitle.toUpperCase()}
                </div>
                <div
                    className="text-xl md:text-2xl font-light"
                    style={{ color: "#E8F4FF" }}
                >
                    {def.title}
                </div>
                <p
                    className="text-xs md:text-sm leading-relaxed mt-2"
                    style={{ color: "rgba(200,220,240,0.72)" }}
                >
                    {def.description}
                </p>
            </div>
        </button>
    )
}

// ── Glifos SVG por modelo ─────────────────────────────────
function ModelGlyph({
    color,
    halo,
    selected,
    kind,
    compact = false,
}: {
    color: string
    halo: string
    selected: boolean
    kind: AvatarModel
    /** 2026-05-05 — Cuando true (cards de Material en mobile horizontal),
     *  el glyph se reduce a 56px para que la card quepa sin sobrar. */
    compact?: boolean
}) {
    const scale = selected ? 1 : 0.92
    const opacity = selected ? 1 : 0.78
    const size = compact ? 56 : 88
    return (
        <div
            className="self-center md:!w-[88px] md:!h-[88px] flex-shrink-0"
            style={{
                width: size,
                height: size,
                opacity,
                transform: `scale(${scale})`,
                transition: "transform 240ms ease, opacity 240ms ease",
            }}
        >
            <svg viewBox="0 0 100 100" width="100%" height="100%">
                <defs>
                    <radialGradient id={`grad-${kind}`} cx="50%" cy="50%">
                        <stop
                            offset="0%"
                            stopColor={color}
                            stopOpacity="0.95"
                        />
                        <stop
                            offset="60%"
                            stopColor={halo}
                            stopOpacity="0.45"
                        />
                        <stop
                            offset="100%"
                            stopColor={halo}
                            stopOpacity="0"
                        />
                    </radialGradient>
                </defs>
                <circle cx="50" cy="50" r="44" fill={`url(#grad-${kind})`} />
                {kind === "cristal" && (
                    <polygon
                        points="50,18 78,34 78,66 50,82 22,66 22,34"
                        fill="none"
                        stroke={color}
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                        opacity="0.95"
                    />
                )}
                {kind === "plasma" && (
                    <>
                        <circle
                            cx="50"
                            cy="50"
                            r="20"
                            fill={color}
                            opacity="0.85"
                        />
                        <ellipse
                            cx="50"
                            cy="50"
                            rx="28"
                            ry="14"
                            fill="none"
                            stroke={halo}
                            strokeWidth="1.4"
                            opacity="0.7"
                            transform="rotate(20 50 50)"
                        />
                        <ellipse
                            cx="50"
                            cy="50"
                            rx="14"
                            ry="28"
                            fill="none"
                            stroke={halo}
                            strokeWidth="1.4"
                            opacity="0.7"
                            transform="rotate(-15 50 50)"
                        />
                    </>
                )}
                {kind === "biopolimero" && (
                    <path
                        d="M30 50 Q40 25, 50 35 T75 50 Q80 70, 60 70 T35 80 Q22 65, 30 50 Z"
                        fill={color}
                        opacity="0.7"
                        stroke={halo}
                        strokeWidth="1.2"
                    />
                )}
            </svg>
        </div>
    )
}

// ── Glifos SVG por polaridad ──────────────────────────────
function PolarityGlyph({
    aura,
    accent,
    selected,
    kind,
}: {
    aura: string
    accent: string
    selected: boolean
    kind: AvatarPolarity
}) {
    const scale = selected ? 1 : 0.9
    const opacity = selected ? 1 : 0.78
    return (
        <div
            style={{
                width: 88,
                height: 88,
                opacity,
                transform: `scale(${scale})`,
                transition: "transform 240ms ease, opacity 240ms ease",
                flexShrink: 0,
            }}
        >
            <svg viewBox="0 0 100 100" width="100%" height="100%">
                <defs>
                    <radialGradient id={`pol-${kind}`} cx="50%" cy="50%">
                        <stop offset="0%" stopColor={aura} stopOpacity="0.95" />
                        <stop
                            offset="55%"
                            stopColor={accent}
                            stopOpacity="0.4"
                        />
                        <stop
                            offset="100%"
                            stopColor={accent}
                            stopOpacity="0"
                        />
                    </radialGradient>
                </defs>
                <circle cx="50" cy="50" r="44" fill={`url(#pol-${kind})`} />
                {kind === "alfa" ? (
                    <>
                        {/* Rayo angular descendente — pulso eléctrico. */}
                        <path
                            d="M52 18 L34 50 L48 50 L42 82 L66 46 L52 46 Z"
                            fill={accent}
                            opacity="0.92"
                            stroke={aura}
                            strokeWidth="1"
                            strokeLinejoin="round"
                        />
                    </>
                ) : (
                    <>
                        {/* Espiral magnética envolvente. */}
                        <path
                            d="M50 22 C68 22 78 36 78 50 C78 64 66 76 50 76 C36 76 26 66 26 54 C26 44 34 36 44 36 C52 36 58 42 58 50 C58 56 54 60 50 60"
                            fill="none"
                            stroke={accent}
                            strokeWidth="3"
                            strokeLinecap="round"
                            opacity="0.9"
                        />
                    </>
                )}
            </svg>
        </div>
    )
}

// ── Style helpers ────────────────────────────────────────
function cardStyle(
    accent: string,
    selected: boolean,
    disabled: boolean
): React.CSSProperties {
    return {
        border: `1px solid ${selected ? accent : "rgba(120,160,200,0.18)"}`,
        background: selected
            ? `linear-gradient(160deg, rgba(8,16,32,0.85) 0%, ${hexA(accent, 0.18)} 100%)`
            : "rgba(8,12,24,0.55)",
        boxShadow: selected
            ? `0 0 32px ${hexA(accent, 0.45)}, inset 0 0 32px ${hexA(accent, 0.12)}`
            : "0 4px 18px rgba(0,0,0,0.35)",
        backdropFilter: "blur(18px) saturate(140%)",
        WebkitBackdropFilter: "blur(18px) saturate(140%)",
        transition:
            "background 240ms ease, border-color 240ms ease, box-shadow 240ms ease",
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled && !selected ? 0.4 : 1,
    }
}

function hexA(hex: string, a: number): string {
    const v = hex.replace("#", "")
    const n = parseInt(
        v.length === 3 ? v.split("").map((c) => c + c).join("") : v,
        16
    )
    const r = (n >> 16) & 0xff
    const g = (n >> 8) & 0xff
    const b = n & 0xff
    return `rgba(${r},${g},${b},${a})`
}
